import type { ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { RequireAuth } from 'grabbitbot-legacy-ui'
import { Shell } from '../layout/Shell'
import { ProjectProvider, RequireProjectAdmin } from '../layout/ProjectContext'
import { POST_LOGIN_PATH } from '../config'
import { FinanceProvider } from '../admin-finance/FinanceProvider'
import { AdminFinanceLayout } from '../admin-finance/AdminFinanceLayout'
import { OverviewPage } from '../admin-finance/OverviewPage'
import { ProblemsPage } from '../admin-finance/ProblemsPage'
import { UserPage as FinanceUserPage } from '../admin-finance/UserPage'
import { AdminOperationsPage, StripeLogPage } from '../admin-finance/LogPages'
import { HelpPage as FinanceHelpPage } from '../admin-finance/HelpPage'

import { LoginPage } from '../pages/auth/LoginPage'
import { RegisterPage } from '../pages/auth/RegisterPage'
import { LogoutPage } from '../pages/auth/LogoutPage'
import { PasswordForgotPage } from '../pages/auth/PasswordForgotPage'
import { PasswordForgotSetPage } from '../pages/auth/PasswordForgotSetPage'
import { RegisterValidatePage } from '../pages/auth/RegisterValidatePage'
import { PasswordSetupValidatePage } from '../pages/auth/PasswordSetupValidatePage'
import { UserPage } from '../pages/account/UserPage'

import { ProjectListPage } from '../pages/project/ProjectListPage'
import { ProjectDashboardPage } from '../pages/project/ProjectDashboardPage'
import { ProjectUserAccessPage } from '../pages/project/ProjectUserAccessPage'
import { ProjectEditPage } from '../pages/project/ProjectEditPage'
import { ServiceAccountsPage } from '../pages/project/ServiceAccountsPage'
import { ConnectionSettingsPage } from '../pages/project/ConnectionSettingsPage'

import { PlanListPage } from '../pages/catalogue/PlanListPage'
import { ProductListPage } from '../pages/catalogue/ProductListPage'
import { UsageFeeListPage } from '../pages/catalogue/UsageFeeListPage'

import { PaymentReturnPage } from '../pages/payment/PaymentReturnPage'
import { PaymentMethodPage } from '../pages/payment/PaymentMethodPage'
import { PaymentBillingPage } from '../pages/payment/PaymentBillingPage'
import { PaymentCreditPage } from '../pages/payment/PaymentCreditPage'
import { PaymentTransactionsPage } from '../pages/payment/PaymentTransactionsPage'
import { PaymentInvoicesPage } from '../pages/payment/PaymentInvoicesPage'

import { TelegramChatUserManagerPage } from '../pages/telegram/TelegramChatUserManagerPage'
import { TelegramConnectionPage } from '../pages/telegram/TelegramConnectionPage'
import { TelegramConnectionCheckPage } from '../pages/telegram/TelegramConnectionCheckPage'

import { AdminInvoiceReportPage } from '../pages/admin/AdminInvoiceReportPage'
import { AdminUserListPage } from '../pages/admin/AdminUserListPage'
import { AdminUserSubscriptionListPage } from '../pages/admin/AdminUserSubscriptionListPage'
import { AdminUserProductListPage } from '../pages/admin/AdminUserProductListPage'
import { AdminUserTransactionListPage } from '../pages/admin/AdminUserTransactionListPage'
import { AdminUserCreditPage } from '../pages/admin/AdminUserCreditPage'
import { AdminUserPaymentInfoPage } from '../pages/admin/AdminUserPaymentInfoPage'
import { AdminUserUsagefeePage } from '../pages/admin/AdminUserUsagefeePage'
import { AdminCataloguePage } from '../pages/admin/AdminCataloguePage'
import { AdminCreditListPage } from '../pages/admin/AdminCreditListPage'

/** Route guard — the fix for audit §10 D1 (no `canActivate` anywhere: every
 * route, admin and payment pages included, rendered for anyone and only its
 * API calls failed). Signed out → `/login`. */
function Protected({ children }: { children: ReactNode }) {
  return (
    <RequireAuth fallback={<Navigate to="/login" replace />} pending={<p>Loading…</p>}>
      <FinanceProvider>
        <Shell>{children}</Shell>
      </FinanceProvider>
    </RequireAuth>
  )
}

/** `/projects/:projectId/*` — loads the project once (its `isAdmin` drives
 * the admin guard and the header menus). */
function ProjectScope() {
  return (
    <Protected>
      <ProjectProvider>
        <Outlet />
      </ProjectProvider>
    </Protected>
  )
}

const admin = (el: ReactNode) => <RequireProjectAdmin>{el}</RequireProjectAdmin>

/**
 * Every legacy route (audit §3 / app-routing.module.ts), same paths. Pages
 * the legacy header showed only to project admins (Config, Admin menus) are
 * admin-guarded; the backend remains the authorization boundary.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* --- public / auth (owner decision 2026-09-27: Auth0 "Payment" app) --- */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/password-forgot" element={<PasswordForgotPage />} />
      <Route path="/password-forgot-set" element={<PasswordForgotSetPage />} />
      <Route path="/register-validate" element={<RegisterValidatePage />} />
      <Route path="/passwordsetup-validate" element={<PasswordSetupValidatePage />} />

      <Route path="/user" element={<Protected><UserPage /></Protected>} />
      <Route path="/connections/telegram" element={<Protected><TelegramConnectionPage /></Protected>} />
      <Route path="/connections/telegram/check" element={<Protected><TelegramConnectionCheckPage /></Protected>} />
      <Route path="/projects" element={<Protected><ProjectListPage /></Protected>} />

      {/* Admin mode (owner decision 2026-10-09 b): finance dashboard for project admins
          (own projects) and the global admin (also "Minden projekt"); read-only GETs. */}
      <Route path="/admin/finance" element={<Protected><AdminFinanceLayout /></Protected>}>
        <Route index element={<OverviewPage />} />
        <Route path="problems" element={<ProblemsPage />} />
        <Route path="users" element={<FinanceUserPage />} />
        <Route path="stripe-log" element={<StripeLogPage />} />
        <Route path="admin-operations" element={<AdminOperationsPage />} />
        <Route path="help" element={<FinanceHelpPage />} />
        <Route path="*" element={<Navigate to="/admin/finance" replace />} />
      </Route>

      <Route path="/projects/:projectId" element={<ProjectScope />}>
        <Route index element={<ProjectDashboardPage />} />
        <Route path="users" element={admin(<ProjectUserAccessPage />)} />
        <Route path="edit" element={admin(<ProjectEditPage />)} />
        <Route path="service-accounts" element={admin(<ServiceAccountsPage />)} />
        <Route path="connection-settings" element={<ConnectionSettingsPage />} />
        <Route path="subscriptions" element={<PlanListPage />} />
        <Route path="subscriptions/:planId" element={<PlanListPage />} />
        <Route path="products" element={<ProductListPage />} />
        <Route path="products/:productId" element={<ProductListPage />} />
        <Route path="usagefees" element={<UsageFeeListPage />} />
        <Route path="usagefees/:usagefeeId" element={<UsageFeeListPage />} />
        <Route path="telegram-chat-user-manager" element={admin(<TelegramChatUserManagerPage />)} />
        <Route path="telegram-chat-user-manager/:managerId" element={admin(<TelegramChatUserManagerPage />)} />
        <Route path="payment/success" element={<PaymentReturnPage mode="success" />} />
        <Route path="payment/cancel" element={<PaymentReturnPage mode="cancel" />} />
        <Route path="payment/method" element={<PaymentMethodPage />} />
        <Route path="payment/billing" element={<PaymentBillingPage />} />
        <Route path="payment/credit" element={<PaymentCreditPage />} />
        <Route path="payment/transactions" element={<PaymentTransactionsPage />} />
        <Route path="payment/invoices" element={<PaymentInvoicesPage />} />
        <Route path="admin/invoices" element={admin(<AdminInvoiceReportPage />)} />
        <Route path="admin/users" element={admin(<AdminUserListPage />)} />
        <Route path="admin/users/subscriptions" element={admin(<AdminUserSubscriptionListPage />)} />
        <Route path="admin/users/products" element={admin(<AdminUserProductListPage />)} />
        <Route path="admin/users/transactions" element={admin(<AdminUserTransactionListPage />)} />
        <Route path="admin/users/credit" element={admin(<AdminUserCreditPage />)} />
        <Route path="admin/users/paymentinfo" element={admin(<AdminUserPaymentInfoPage />)} />
        <Route path="admin/users/usagefees" element={admin(<AdminUserUsagefeePage />)} />
        <Route path="admin/plans" element={admin(<AdminCataloguePage kind="plan" />)} />
        <Route path="admin/plans/:planId" element={admin(<AdminCataloguePage kind="plan" />)} />
        <Route path="admin/products" element={admin(<AdminCataloguePage kind="product" />)} />
        <Route path="admin/products/:productId" element={admin(<AdminCataloguePage kind="product" />)} />
        <Route path="admin/credits" element={admin(<AdminCreditListPage />)} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Route>

      <Route path="/" element={<Navigate to={POST_LOGIN_PATH} replace />} />
      <Route path="*" element={<Navigate to={POST_LOGIN_PATH} replace />} />
    </Routes>
  )
}
